import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Wifi,
    UtensilsCrossed,
    Wine,
    Waves,
    Car,
    ArrowRight,
    Star,
    MapPin,
    Umbrella,
    Users,
    Martini,
} from "lucide-react";
import { useLang, tField } from "../lib/LanguageContext";
import { useCurrency } from "../lib/CurrencyContext";
import { api, imageUrl, whatsappLink } from "../lib/api";
import { Button } from "../components/ui/button";
import Newsletter from "../components/Newsletter";
import PressSection from "../components/PressSection";
import InstagramFeed from "../components/InstagramFeed";
import SEO, { hotelSchema, reviewsSchema } from "../components/SEO";

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
};

export default function Home() {
    const { t, lang } = useLang();
    const { format } = useCurrency();
    const { site } = useOutletContext() || {};
    const [rooms, setRooms] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [gallery, setGallery] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/rooms"),
            api.get("/facilities"),
            api.get("/reviews"),
            api.get("/gallery"),
        ])
            .then(([r, f, rv, g]) => {
                setRooms(r.data.filter((x) => x.is_featured).slice(0, 3));
                setFacilities(f.data);
                setReviews(rv.data);
                setGallery(g.data.slice(0, 6));
            })
            .catch(() => {});
    }, []);

    const heroImage = imageUrl(site?.hero_image, imageUrl("seed:hero"));
    const heroVideo = site?.hero_video || null;
    const heroTitle = tField(site, "hero_title", lang) || "Kompleksi Dea Radhimë";
    const heroSubtitle =
        tField(site, "hero_subtitle", lang) || t("hero_subtitle_default");
    const aboutText = tField(site, "about", lang);
    const whatsapp = site?.whatsapp || "355682116731";

    const quickInfo = [
        { icon: Wifi, key: "wifi" },
        { icon: UtensilsCrossed, key: "restaurant" },
        { icon: Wine, key: "bar" },
        { icon: Waves, key: "beach" },
        { icon: Car, key: "parking" },
    ];

    const highlights = [
        { icon: Waves, key: "hl_beach_distance" },
        { icon: Umbrella, key: "hl_private_beach" },
        { icon: Users, key: "hl_family" },
        { icon: Martini, key: "hl_bar_restaurant" },
    ];

    return (
        <div data-testid="home-page">
            <SEO
                description={lang === "al" ? "Hotel luksoz buzë detit në Radhimë, Vlorë — dhoma me pamje deti, restorant, bar, plazh privat, parking falas. Rezervo në WhatsApp." : "Luxury seaside hotel in Radhimë, Vlorë — seaview rooms, restaurant, bar, private beach, free parking. Book via WhatsApp."}
                path="/"
                image={imageUrl(site?.hero_image, imageUrl("seed:hero"))}
                schemas={[hotelSchema({ site, rooms, reviews }), reviewsSchema(reviews)]}
            />
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-screen flex items-end overflow-hidden"
            >
                {heroVideo ? (
                    <video
                        src={heroVideo.startsWith("http") ? heroVideo : imageUrl(heroVideo)}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                        poster={heroImage}
                    />
                ) : (
                    <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${heroImage})` }}
                    />
                )}
                <div className="absolute inset-0 hero-gradient" />

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        className="max-w-3xl"
                    >
                        <div className="flex items-center gap-2 text-white/80 text-sm tracking-[0.3em] uppercase mb-6">
                            <MapPin size={14} strokeWidth={1.5} />
                            <span>Radhimë · Vlorë · Albania</span>
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
                        >
                            {heroTitle}
                        </h1>
                        <p className="text-white/90 text-lg md:text-xl font-light mt-6 max-w-2xl leading-relaxed">
                            {heroSubtitle}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-10">
                            <Link to="/book" data-testid="hero-book-now">
                                <Button className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-8 py-6 rounded-full text-sm tracking-wide font-medium">
                                    {t("book_now")}
                                    <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/rooms">
                                <Button
                                    variant="outline"
                                    data-testid="hero-explore-rooms"
                                    className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#0F3A59] backdrop-blur-md px-8 py-6 rounded-full text-sm tracking-wide font-medium"
                                >
                                    {t("nav_rooms")}
                                </Button>
                            </Link>
                        </div>

                        {/* Mini Highlights */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
                            data-testid="hero-highlights"
                            className="mt-12 md:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-3xl"
                        >
                            {highlights.map((h, i) => (
                                <motion.div
                                    key={h.key}
                                    whileHover={{ scale: 1.04, y: -3 }}
                                    transition={{ type: "spring", stiffness: 280, damping: 20 }}
                                    data-testid={`highlight-${h.key}`}
                                    className="group bg-white/12 backdrop-blur-xl border border-white/25 rounded-2xl px-3 py-5 sm:px-4 sm:py-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-white/20 hover:border-white/40 transition-colors duration-300 cursor-default"
                                >
                                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/15 mb-3 group-hover:bg-[#CBA871]/30 transition-colors duration-300">
                                        <h.icon
                                            className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-white"
                                            strokeWidth={1.6}
                                        />
                                    </div>
                                    <div className="text-white font-semibold text-[13px] sm:text-sm leading-snug tracking-tight">
                                        {t(h.key)}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* QUICK INFO STRIP */}
            <section className="bg-white border-y border-[#E5E0D8] py-10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 sm:grid-cols-5 gap-6 md:gap-8">
                    {quickInfo.map((item, i) => (
                        <motion.div
                            key={item.key}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeUp}
                            transition={{ delay: i * 0.08, duration: 0.5 }}
                            className="flex flex-col items-center text-center"
                            data-testid={`quick-info-${item.key}`}
                        >
                            <item.icon
                                size={28}
                                strokeWidth={1.3}
                                className="text-[#0F3A59] mb-2"
                            />
                            <span className="text-xs sm:text-sm tracking-wide font-light text-[#1C1C1C]">
                                {t(item.key)}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ABOUT / INTRO */}
            {aboutText && (
                <section className="py-24 md:py-32 px-6 md:px-12 max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="w-12 h-px bg-[#CBA871] mx-auto mb-6" />
                        <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-[#0F3A59] leading-tight">
                            {lang === "al"
                                ? "Një strehë bregdetare e kuruar"
                                : "A coastal retreat, crafted with care"}
                        </h2>
                        <p className="mt-8 text-[#1C1C1C]/75 text-base md:text-lg font-light leading-relaxed">
                            {aboutText}
                        </p>
                    </motion.div>
                </section>
            )}

            {/* FEATURED ROOMS */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-[#F4EFE6]">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4">
                        <div>
                            <span className="text-xs tracking-[0.3em] uppercase text-[#0F3A59]/60">
                                01 — {t("nav_rooms")}
                            </span>
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0F3A59] mt-2">
                                {t("featured_rooms")}
                            </h2>
                            <p className="text-[#1C1C1C]/70 font-light mt-3 max-w-xl">
                                {t("featured_rooms_sub")}
                            </p>
                        </div>
                        <Link
                            to="/rooms"
                            data-testid="home-view-all-rooms"
                            className="text-[#0F3A59] text-sm font-medium tracking-wide flex items-center gap-2 hover:gap-3 transition-all border-b border-[#0F3A59] pb-1"
                        >
                            {t("view_all_rooms")}
                            <ArrowRight size={16} />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {rooms.map((r, i) => (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                data-testid={`featured-room-${r.slug}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img decoding="async" loading="lazy"
                                        src={imageUrl(
                                            r.images?.[0],
                                            imageUrl("seed:room_standard"),
                                        )}
                                        alt={tField(r, "title", lang)}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <h3 className="font-serif text-2xl text-[#0F3A59]">
                                        {tField(r, "title", lang)}
                                    </h3>
                                    <p className="text-[#1C1C1C]/65 font-light text-sm mt-2 line-clamp-2">
                                        {tField(r, "description", lang)}
                                    </p>
                                    <div className="flex items-end justify-between mt-6 pt-6 border-t border-[#E5E0D8]">
                                        <div>
                                            <div className="text-xs text-[#1C1C1C]/60 uppercase tracking-wider">
                                                {t("from_price")}
                                            </div>
                                            <div className="font-serif text-2xl text-[#0F3A59]">
                                                {format(r.price)}
                                                <span className="text-sm font-sans text-[#1C1C1C]/60 ml-1">
                                                    {t("per_night")}
                                                </span>
                                            </div>
                                        </div>
                                        <Link
                                            to={`/book?room=${r.slug}`}
                                            className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-5 py-2.5 rounded-full text-xs tracking-wide transition-colors"
                                            data-testid={`book-room-${r.slug}`}
                                        >
                                            {t("book_now")}
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FACILITIES PREVIEW */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-xs tracking-[0.3em] uppercase text-[#0F3A59]/60">
                            02 — {t("nav_facilities")}
                        </span>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0F3A59] mt-2">
                            {t("explore_facilities")}
                        </h2>
                        <p className="text-[#1C1C1C]/70 font-light mt-3 max-w-xl mx-auto">
                            {t("facilities_sub")}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {facilities.map((f, i) => (
                            <motion.div
                                key={f.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                data-testid={`facility-preview-${f.slug}`}
                                className="group relative aspect-[4/5] rounded-2xl overflow-hidden"
                            >
                                <img decoding="async" loading="lazy"
                                    src={imageUrl(
                                        f.image,
                                        imageUrl("seed:facility_restaurant"),
                                    )}
                                    alt={tField(f, "title", lang)}
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                <div className="absolute bottom-0 p-8 text-white w-full">
                                    <h3 className="font-serif text-3xl">
                                        {tField(f, "title", lang)}
                                    </h3>
                                    <p className="text-sm text-white/80 font-light mt-2 line-clamp-2">
                                        {tField(f, "description", lang)}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* REVIEWS */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0F3A59] text-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <span className="text-xs tracking-[0.3em] uppercase text-white/60">
                            03 — Reviews
                        </span>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
                            {t("reviews_title")}
                        </h2>
                        <p className="text-white/70 font-light mt-3">
                            {t("reviews_sub")}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {reviews.slice(0, 3).map((rv, i) => (
                            <motion.div
                                key={rv.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                data-testid={`review-${i}`}
                                className="bg-white/5 border border-white/10 backdrop-blur-md rounded-2xl p-8"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex gap-1">
                                        {[...Array(rv.rating || 5)].map((_, idx) => (
                                            <Star
                                                key={`star-${idx}`}
                                                size={14}
                                                className="fill-[#CBA871] text-[#CBA871]"
                                            />
                                        ))}
                                    </div>
                                    {rv.source && rv.source !== "direct" && (
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                                            {rv.source}
                                        </span>
                                    )}
                                </div>
                                <p className="text-white/85 font-light italic leading-relaxed">
                                    "{tField(rv, "text", lang)}"
                                </p>
                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <div className="font-serif text-lg">{rv.name}</div>
                                    {rv.country && (
                                        <div className="text-xs text-white/50 tracking-wider uppercase mt-0.5">
                                            {rv.country}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GALLERY PREVIEW */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <span className="text-xs tracking-[0.3em] uppercase text-[#0F3A59]/60">
                                04 — {t("nav_gallery")}
                            </span>
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0F3A59] mt-2">
                                {t("gallery_title")}
                            </h2>
                            <p className="text-[#1C1C1C]/70 font-light mt-3">
                                {t("gallery_sub")}
                            </p>
                        </div>
                        <Link
                            to="/gallery"
                            data-testid="home-view-gallery"
                            className="text-[#0F3A59] text-sm font-medium tracking-wide flex items-center gap-2 hover:gap-3 transition-all border-b border-[#0F3A59] pb-1"
                        >
                            {t("view_gallery")}
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                        {(gallery.length
                            ? gallery
                            : [
                                  { id: "1", image: "seed:gallery_beach" },
                                  { id: "2", image: "seed:gallery_pool" },
                                  { id: "3", image: "seed:gallery_exterior" },
                                  { id: "4", image: "seed:room_gabina" },
                                  { id: "5", image: "seed:facility_bar" },
                                  { id: "6", image: "seed:room_apartment" },
                              ]
                        )
                            .slice(0, 6)
                            .map((g, i) => (
                                <motion.div
                                    key={g.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.06, duration: 0.5 }}
                                    className={`gallery-item rounded-xl overflow-hidden ${
                                        i === 0 || i === 5 ? "md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"
                                    }`}
                                >
                                    <img decoding="async" loading="lazy"
                                        src={imageUrl(g.image)}
                                        alt=""
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                    />
                                </motion.div>
                            ))}
                    </div>
                </div>
            </section>

            {/* PRESS / FEATURED IN */}
            <PressSection />

            {/* INSTAGRAM FEED */}
            <InstagramFeed posts={site?.instagram_posts || []} handle={site?.instagram || ""} />

            {/* NEWSLETTER */}
            <Newsletter />
        </div>
    );
}
